import type { PolymorphicRef } from '@sds-system/types';
import type { ElementType } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import type { TypographyOptions } from '../Typography';
import { Typography } from '../Typography';
import {
  StyledDot,
  StyledIndicatorCircle,
  StyledIndicatorWrapper,
  StyledNumber,
} from './Indicator.styled';
import type { IndicatorComponent, IndicatorProps, StyledSizeProps } from './Indicator.types';

const numberBoldSizeVariant: Record<
  Exclude<StyledSizeProps['size'], undefined>,
  TypographyOptions['fontType']
> = {
  S: 'caption.bold',
  M: 'body2.bold',
  L: 'body2.bold',
};
const numbermediumSizeVariant: Record<
  Exclude<StyledSizeProps['size'], undefined>,
  TypographyOptions['fontType']
> = {
  S: 'caption.medium',
  M: 'body2.medium',
  L: 'body2.medium',
};

export const Indicator: IndicatorComponent = forwardRef(
  <C extends ElementType = 'div'>(
    {
      current = 1,
      variant = 'dot',
      totalLength,
      size = 'M',
      onChange,
      styledComponents,
      ...props
    }: IndicatorProps<C>,
    ref: PolymorphicRef<C>,
  ) => {
    const [currentPage, setCurrentPage] = useState(current);

    const handleClickDot = (index: number) => () => {
      setCurrentPage(index);
      onChange?.(index);
    };

    useEffect(() => {
      setCurrentPage(current);
      onChange?.(current);
    }, [current]);

    return (
      <StyledIndicatorWrapper
        display='flex'
        gap={8}
        ref={ref}
        size={size}
        variant={variant}
        {...styledComponents?.root}
        {...props}
      >
        {totalLength && variant === 'dot' ? (
          Array.from({ length: totalLength }, (_, index) => (
            <StyledDot
              bgColor={currentPage === index + 1 ? 'bg.interactive.primary' : 'bg.dimmer.40'}
              className={currentPage === index + 1 ? 'indicatorDotActive' : 'indicatorDot'}
              // biome-ignore lint/suspicious/noArrayIndexKey: unique 값 없음
              key={index}
              onClick={handleClickDot(index + 1)}
              size={size}
              {...styledComponents?.dot}
            />
          ))
        ) : (
          <StyledNumber
            alignItems='center'
            display='flex'
            size={size}
            variant={variant}
            {...styledComponents?.numberWrapper}
          >
            <Typography
              className='currentNumber'
              color={
                variant === 'number.line' ? 'text.interactive.primary' : 'text.interactive.inverse'
              }
              fontType={numberBoldSizeVariant[size]}
            >
              {currentPage}
            </Typography>
            <Typography
              className={variant === 'number.line' ? '' : 'middle'}
              fontType={numbermediumSizeVariant[size]}
            >
              {variant === 'number.line' ? <StyledIndicatorCircle className='middle' /> : '/'}
            </Typography>
            <Typography className='totalNumber' fontType={numbermediumSizeVariant[size]}>
              {totalLength}
            </Typography>
          </StyledNumber>
        )}
      </StyledIndicatorWrapper>
    );
  },
);
